import { useMemo, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import type { ClinicOutgoingOrder } from "@/lib/dashboard-lab";
import { cn } from "@/lib/cn";
import { CatalogoBusca, combina } from "@/components/catalogo/CatalogoBusca";
import { GRUPOS, entregaDe, grupoDe, recusadoRecente } from "./resumo-de-pedidos";
import { CartaoDoResumoDaClinica } from "./CartaoDoResumoDaClinica";

const MAX_POR_GRUPO = 8;

// As colunas dividem a largura toda entre os grupos que têm pedido; com um grupo só, os cartões
// é que se dividem em colunas.
const COLUNAS: Record<number, string> = { 1: "", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-2 2xl:grid-cols-4" };

type Props = {
  orders: ClinicOutgoingOrder[];
  onSelectOrder: (order: ClinicOutgoingOrder) => void;
};

/** Resumo dos pedidos enviados pela clínica, pelo dia de entrega — a versão da clínica do
 *  "Resumo por prazo" do laboratório, com o selo de aceite em cada pedido. */
export function ResumoDePedidosDaClinica({ orders: todos, onSelectOrder }: Props) {
  // Busca por paciente: filtra na tela e, buscando, mostra todos os achados (sem o limite por coluna).
  const [busca, setBusca] = useState("");
  const orders = useMemo(() => todos.filter((o) => combina(o.patientName, busca.trim())), [todos, busca]);
  const limite = busca.trim() ? Infinity : MAX_POR_GRUPO;
  const grupos = useMemo(
    () =>
      GRUPOS.map((g) => {
        const linhas = orders
          .filter((o) => grupoDe(o) === g.chave)
          .sort((a, b) => entregaDe(a).getTime() - entregaDe(b).getTime());
        return { ...g, linhas: linhas.slice(0, limite), total: linhas.length };
      }).filter((g) => g.total > 0),
    [orders, limite],
  );
  const recusados = useMemo(() => orders.filter(recusadoRecente), [orders]);
  const aguardando = orders.filter((o) => o.status === "AGUARDANDO_CONFIRMACAO").length;
  // Dentista só quando a lista tem mais de um — no consultório de uma pessoa é sempre ela.
  const comDentista = new Set(todos.map((o) => o.dentistName).filter(Boolean)).size > 1;

  const campo = <CatalogoBusca valor={busca} onChange={setBusca} placeholder="Buscar paciente" achados={grupos.reduce((n, g) => n + g.total, 0) + recusados.length} className="mt-0 sm:max-w-xs" />;

  if (busca.trim() && grupos.length === 0 && recusados.length === 0) {
    return (
      <div className="space-y-4">
        {campo}
        <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum pedido em aberto desse paciente.</p>
      </div>
    );
  }

  if (grupos.length === 0 && recusados.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 px-6 py-10 text-center dark:border-border dark:bg-muted/20">
        <ClipboardCheck className="mx-auto h-9 w-9 text-zinc-300 dark:text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-foreground">Nenhum pedido em aberto</p>
        <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
          Os pedidos enviados aos laboratórios aparecem aqui, pelo dia de entrega.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {campo}
      {aguardando > 0 && (
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
          <span className="font-semibold text-brand-700 dark:text-primary">{aguardando}</span>{" "}
          {aguardando === 1 ? "pedido aguardando o aceite do laboratório" : "pedidos aguardando o aceite do laboratório"}
        </p>
      )}

      {grupos.length > 0 && (
        <div className={cn("grid grid-cols-1 gap-4", COLUNAS[grupos.length])}>
          {grupos.map((g) => (
            <div key={g.chave} className="rounded-xl border border-zinc-200 p-3.5 dark:border-border">
              <p
                className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide"
                style={{ color: g.cor }}
              >
                {g.rotulo}
                <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {g.total}
                </span>
              </p>
              <div className={cn("grid grid-cols-1 gap-1.5", grupos.length === 1 && "md:grid-cols-2 xl:grid-cols-3")}>
                {g.linhas.map((o) => (
                  <CartaoDoResumoDaClinica key={o.id} order={o} comDentista={comDentista} onClick={() => onSelectOrder(o)} />
                ))}
              </div>
              {g.total > g.linhas.length && (
                <p className="pt-2 text-center text-xs text-zinc-400 dark:text-muted-foreground">
                  +{g.total - g.linhas.length} em Prótese
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {recusados.length > 0 && (
        <div className="rounded-xl border border-zinc-200 p-3.5 dark:border-border">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
            Recusados nos últimos 30 dias
          </p>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-3">
            {recusados.map((o) => (
              <CartaoDoResumoDaClinica key={o.id} order={o} comDentista={comDentista} onClick={() => onSelectOrder(o)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
